/**
 * Tenant Context
 * 
 * Manages tenant state and branding across the application
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import tenantService from '../services/tenantService';

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const [currentTenant, setCurrentTenant] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Load current tenant information
   */
  const loadCurrentTenant = async () => {
    try {
      setLoading(true);
      const response = await tenantService.getCurrentTenant();
      if (response.success && response.data.tenant) {
        setCurrentTenant(response.data.tenant);
        applyTenantBranding(response.data.tenant);
      } else {
        // No tenant context (platform-admin or legacy mode)
        setCurrentTenant(null);
      }
    } catch (err) {
      console.error('Failed to load current tenant:', err);
      setError(err.message);
      setCurrentTenant(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load user's tenant memberships
   */
  const loadMemberships = async () => {
    try {
      const response = await tenantService.getUserMemberships();
      if (response.success) {
        setMemberships(response.data.memberships);
      }
    } catch (err) {
      console.error('Failed to load memberships:', err);
    }
  };

  /**
   * Switch to a different tenant
   */
  const switchTenant = async (tenantId) => {
    try {
      setLoading(true);
      const response = await tenantService.switchTenant(tenantId);
      
      if (response.success) {
        // Update token in localStorage
        localStorage.setItem('token', response.token);
        
        // Update current tenant
        setCurrentTenant(response.tenant);
        applyTenantBranding(response.tenant);
        
        // Reload the page to refresh all data with new tenant context
        window.location.reload();
      }
    } catch (err) {
      console.error('Failed to switch tenant:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Apply tenant branding to the application
   */
  const applyTenantBranding = (tenant) => {
    if (!tenant) return;

    // Update page title
    document.title = `${tenant.name} - EDUNA`;

    // Apply primary color to CSS variables (API returns snake_case)
    if (tenant.primary_color) {
      document.documentElement.style.setProperty('--tenant-primary-color', tenant.primary_color);
      document.documentElement.style.setProperty('--cui-primary', tenant.primary_color);
      document.documentElement.style.setProperty('--cui-primary-rgb', hexToRgb(tenant.primary_color));
      // Also set for buttons, links, etc.
      document.documentElement.style.setProperty('--cui-link-color', tenant.primary_color);
      document.documentElement.style.setProperty('--cui-btn-bg', tenant.primary_color);
    }

    // Apply secondary color
    if (tenant.secondary_color) {
      document.documentElement.style.setProperty('--tenant-secondary-color', tenant.secondary_color);
      document.documentElement.style.setProperty('--cui-secondary', tenant.secondary_color);
    }

    // Apply accent color
    if (tenant.accent_color) {
      document.documentElement.style.setProperty('--tenant-accent-color', tenant.accent_color);
    }

    // Apply background color
    if (tenant.background_color) {
      document.documentElement.style.setProperty('--tenant-background-color', tenant.background_color);
    }

    // Apply text color
    if (tenant.text_color) {
      document.documentElement.style.setProperty('--tenant-text-color', tenant.text_color);
    }

    // Update favicon if tenant has favicon or logo
    const faviconUrl = tenant.favicon || tenant.logo;
    if (faviconUrl) {
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) {
        favicon.href = faviconUrl;
      }
    }
  };

  /**
   * Convert hex color to RGB values
   */
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
    }
    return '79, 70, 229'; // Default primary color RGB
  };

  /**
   * Clear tenant context (on logout)
   */
  const clearTenantContext = () => {
    setCurrentTenant(null);
    setMemberships([]);
    setError(null);
  };

  // Load tenant on mount if user is authenticated
  useEffect(() => {
    // Check both possible token names for compatibility
    const token = localStorage.getItem('eduna-token') || localStorage.getItem('token');
    if (token) {
      loadCurrentTenant();
      loadMemberships();
    } else {
      setLoading(false);
    }
  }, []);

  const value = {
    currentTenant,
    memberships,
    loading,
    error,
    loadCurrentTenant,
    loadMemberships,
    switchTenant,
    clearTenantContext,
    applyTenantBranding
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
